/**
 * Shown in place of a module the workspace has not bought.
 *
 * Phase 3 Package 5 (.claude/specs/module-ui-rollout.md AC9-AC11).
 *
 * Rendered AT the module's own route, not by redirecting to a marketing page.
 * The URL, the nav position and the back button all behave normally; only the
 * body differs. A locked module is a storefront, not an error state — 403 and
 * "page not found" both tell the user something is broken, when the truth is
 * simply that this part was never purchased.
 */
import React from "react";
import { useNavigate } from "react-router-dom";

import { ModuleKey, MODULE_LABELS, MODULE_COLORS } from "../moduleMetadata";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { getModuleCommerce } from "../api";

/** What each module actually does, in the user's terms rather than ours. */
const PITCH: Record<ModuleKey, { blurb: string; features: string[] }> = {
  recruitment: {
    blurb: "Post jobs, screen applicants with AI, run interviews and send offers — all against the people data you already keep here.",
    features: ["Job posting & careers page", "AI CV scoring", "Interview scheduling", "Offer letters & e-signing"],
  },
  finance: {
    blurb: "Quotations through to receipts, plus vendors and bills, following the Thai tax-document chain.",
    features: ["Quotations & tax invoices", "Receipts & credit notes", "Vendors & bills", "Product catalog"],
  },
  payroll: {
    blurb: "Run payroll from the attendance and leave data already in your workspace, and issue payslips your team can read here.",
    features: ["Pay runs", "Payslips", "Statutory deductions", "Payroll reports"],
  },
  performance: {
    blurb: "Goal cycles, reviews and continuous feedback, alongside the employee records you already have.",
    features: ["Goal cycles", "360° reviews", "Check-ins", "Calibration"],
  },
  ai_expense_capture: {
    blurb: "Photograph a receipt and let AI read it — amount, vendor, tax — then review and file it.",
    features: ["Receipt OCR", "Bulk upload", "Spend insights", "Budget tracking"],
  },
  esign: {
    blurb: "Upload any PDF and route it for signature, with a full audit trail. Offer letters and HR letters already sign without this.",
    features: ["Upload any PDF", "Signature routing", "Templates", "Audit trail"],
  },
};

export default function ModuleUpsellPage({ module }: { module: ModuleKey }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const label = MODULE_LABELS[module];
  const color = MODULE_COLORS[module];
  const pitch = PITCH[module];

  // Only an admin can buy, and only an admin may read billing. For everyone else the
  // honest page is "ask your admin" — quoting a price to someone who cannot act on it
  // just moves the dead end one screen later.
  const canBuy = user?.role === "admin" || user?.is_super_admin;
  const { data } = useQuery({
    queryKey: ["module-commerce"], queryFn: getModuleCommerce,
    enabled: !!canBuy, retry: false,
  });

  const entry = (data?.modules ?? []).find((m: any) => m.key === module);
  const price: number | null = entry?.price_usd ?? null;
  const per = data?.billing_period === "annual" ? "/yr" : "/mo";
  // A module with no configured price still shows no purchase control — the Phase 4
  // guarantee has to survive prices being added.
  const purchasable = !!entry?.purchasable && price !== null;

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      minHeight: "60vh", padding: "40px 20px",
    }}>
      <div style={{ maxWidth: 480, textAlign: "center" }}>
        <div style={{
          width: 52, height: 52, borderRadius: 13, margin: "0 auto 16px",
          display: "grid", placeItems: "center", background: color,
          color: "white", fontSize: 21, fontWeight: 700,
        }}>
          {label.charAt(0)}
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 750, color: "#0f172a", margin: "0 0 8px" }}>
          {label} isn't on your plan yet
        </h1>

        <p style={{ fontSize: 15, color: "#334155", lineHeight: 1.6, margin: "0 0 20px" }}>
          {pitch.blurb}
        </p>

        <div style={{
          display: "flex", flexWrap: "wrap", gap: 7,
          justifyContent: "center", marginBottom: 24,
        }}>
          {pitch.features.map((f) => (
            <span key={f} style={{
              fontSize: 12, background: "#f1f5f9", border: "1px solid #e2e8f0",
              borderRadius: 999, padding: "5px 11px", color: "#334155",
            }}>
              {f}
            </span>
          ))}
        </div>

        {canBuy && purchasable && (
          <div style={{ fontSize: 22, fontWeight: 750, color: "#0f172a", marginBottom: 20,
                        fontVariantNumeric: "tabular-nums" }}>
            ${price}<span style={{ fontSize: 14, fontWeight: 600, color: "#64748b" }}>{per}</span>
            <div style={{ fontSize: 12, color: "#64748b", fontWeight: 500, marginTop: 2 }}>
              on top of your {data?.plan ?? ""} plan
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          {canBuy && purchasable ? (
            <button
              type="button"
              onClick={() => navigate("/settings?tab=modules")}
              style={{
                background: "#1e3a8a", color: "white", border: "none",
                padding: "11px 20px", borderRadius: 6, fontSize: 14, fontWeight: 650,
                minHeight: 44, cursor: "pointer",
              }}
            >
              Add {label}
            </button>
          ) : (
            <a
              href={`mailto:sales@hrxai.app?subject=${encodeURIComponent(`Adding the ${label} module`)}`}
              style={{
                background: "#1e3a8a", color: "white", textDecoration: "none",
                padding: "11px 20px", borderRadius: 6, fontSize: 14, fontWeight: 650,
                minHeight: 44, display: "inline-flex", alignItems: "center",
              }}
            >
              {canBuy ? `Talk to us about ${label}` : `Ask your admin about ${label}`}
            </a>
          )}
          <button
            type="button"
            onClick={() => navigate("/")}
            style={{
              background: "white", border: "1px solid #cbd5e1", color: "#334155",
              padding: "11px 20px", borderRadius: 6, fontSize: 14, fontWeight: 650,
              cursor: "pointer", minHeight: 44,
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
